/**
 * Calculate distance between two points using Haversine formula
 * @returns distance in meters
 */
export function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Find the nearest point on a route to the user's position
 * @param userLat User latitude
 * @param userLon User longitude
 * @param routePoints Array of [lon, lat] pairs (GeoJSON format)
 * @returns Object with index of nearest point and distance in meters
 */
export function findNearestPointOnRoute(
  userLat: number,
  userLon: number,
  routePoints: [number, number][]
): { index: number; distance: number } {
  let minDistance = Infinity;
  let nearestIndex = 0;

  for (let i = 0; i < routePoints.length; i++) {
    const [lon, lat] = routePoints[i];
    const distance = haversineDistance(userLat, userLon, lat, lon);

    if (distance < minDistance) {
      minDistance = distance;
      nearestIndex = i;
    }
  }

  return { index: nearestIndex, distance: minDistance };
}

/**
 * Calculate bearing between two points
 * @returns bearing in degrees (0-360)
 */
export function calculateBearing(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;

  const dLon = toRad(lon2 - lon1);
  const lat1Rad = toRad(lat1);
  const lat2Rad = toRad(lat2);

  const y = Math.sin(dLon) * Math.cos(lat2Rad);
  const x =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);

  let bearing = toDeg(Math.atan2(y, x));
  return (bearing + 360) % 360;
}

/**
 * Calculate remaining distance along route from a given point index
 * @param routePoints Array of [lon, lat] pairs
 * @param startIndex Index to start from
 * @returns remaining distance in meters
 */
export function calculateRemainingDistance(
  routePoints: [number, number][],
  startIndex: number
): number {
  let distance = 0;

  for (let i = startIndex; i < routePoints.length - 1; i++) {
    const [lon1, lat1] = routePoints[i];
    const [lon2, lat2] = routePoints[i + 1];
    distance += haversineDistance(lat1, lon1, lat2, lon2);
  }

  return distance;
}

/**
 * Format distance for display
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)} м`;
  }
  return `${(meters / 1000).toFixed(1)} км`;
}

/**
 * Format time for display
 */
export function formatTime(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} мин`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} ч`;
  }
  return `${hours} ч ${remainingMinutes} мин`;
}

/**
 * Calculate ETA
 */
export function calculateETA(remainingSeconds: number): Date {
  return new Date(Date.now() + remainingSeconds * 1000);
}

/**
 * Format ETA for display
 */
export function formatETA(date: Date): string {
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}
